import { useState } from "react";
import type { ComponentProps } from "./index";
import { coerceFieldValue, defaultFieldValue, parseFields, type FieldValue } from "./formFields";
import { GONE_NOTICE, isGone, isGoneStatus, parseInteractiveResult, submitInteractiveResult } from "./interactive";
import { BasicControl, FieldRow } from "./FormField";
import { chineseOrdinal, moveItem, parseItems, removeItem, shapePlanResult, type PlanItem } from "./planItems.ts";

type SubmitState = { phase: "idle" } | { phase: "submitting" } | { phase: "submitted" } | { phase: "failed"; status: number; message: string };

/**
 * `confirm_plan` (ADR-0038): a project's title, its direction as a form, and an ordered list the user
 * edits, removes from and reorders, confirmed with one press. Like Form, its arguments are the
 * model's draft and ride along as data. After an answer the card shows what was submitted, read-only.
 */
export function Plan({ sessionId, callId, args, result }: ComponentProps): React.ReactNode {
	const prompt = typeof args.prompt === "string" ? args.prompt : undefined;
	const itemsLabel = typeof args.itemsLabel === "string" ? args.itemsLabel : "计划";
	/** What one item is called, numbered after it: 里程碑一, 里程碑二. */
	const itemName = typeof args.itemName === "string" ? args.itemName : "";
	const nameLabel = typeof args.nameLabel === "string" ? args.nameLabel : "";
	const detailLabel = typeof args.detailLabel === "string" ? args.detailLabel : "";
	const parsedFields = parseFields(args.fields);
	const parsedItems = parseItems(args.items);

	// `args` streams in after mount, so the draft is read on every render and the user's changes sit
	// on top of it: the title and the list once touched, the fields one by one.
	const [titleEdit, setTitleEdit] = useState<string>();
	const [state, setState] = useState<Record<string, FieldValue>>({});
	const [itemsEdit, setItemsEdit] = useState<PlanItem[]>();
	const [submit, setSubmit] = useState<SubmitState>({ phase: "idle" });
	const title = titleEdit ?? (typeof args.title === "string" ? args.title : "");
	const items = itemsEdit ?? (parsedItems.ok ? parsedItems.items : []);

	if (!parsedFields.ok) return <div className="component error">{parsedFields.error}</div>;
	if (!parsedItems.ok) return <div className="component error">{parsedItems.error}</div>;
	const fields = parsedFields.fields;

	const answered = result ? parseInteractiveResult(result) : undefined;
	const submitted = answered?.kind === "value" ? answered.value : undefined;
	const gone = isGone(answered, submit.phase === "failed" ? submit.status : undefined);
	const locked = answered !== undefined || submit.phase === "submitting" || submit.phase === "submitted" || (submit.phase === "failed" && isGoneStatus(submit.status));

	const shownTitle = typeof submitted?.title === "string" ? submitted.title : title;
	const submittedItems = submitted ? parseItems(submitted.items) : undefined;
	const shownItems = submittedItems?.ok ? submittedItems.items : items;
	const direction = submitted?.direction as Record<string, unknown> | undefined;

	function setItems(change: (prev: PlanItem[]) => PlanItem[]) {
		setItemsEdit((prev) => change(prev ?? items));
	}

	function editItem(index: number, patch: Partial<PlanItem>) {
		setItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
	}

	async function onSubmit() {
		setSubmit({ phase: "submitting" });
		const outcome = await submitInteractiveResult(sessionId, callId, shapePlanResult(title, fields, state, items));
		setSubmit(outcome.ok ? { phase: "submitted" } : { phase: "failed", status: outcome.status, message: outcome.message });
	}

	return (
		<div className="component form-component plan-component">
			{prompt && <p className="component-prompt">{prompt}</p>}
			<div className="form-fields">
				<FieldRow label="名称">
					<input type="text" value={shownTitle} disabled={locked} onChange={(e) => setTitleEdit(e.target.value)} />
				</FieldRow>
				{fields.map((f) => (
					<FieldRow key={f.name} label={f.label} hint={f.hint}>
						<BasicControl
							field={f}
							value={direction ? coerceFieldValue(f, direction[f.name]) : (state[f.name] ?? defaultFieldValue(f))}
							disabled={locked}
							onChange={(v) => setState((prev) => ({ ...prev, [f.name]: v }))}
						/>
					</FieldRow>
				))}
			</div>

			<div className="plan-items">
				<div className="plan-items-label">{itemsLabel}</div>
				<ol>
					{shownItems.map((item, i) => (
						<li key={i} className="plan-item">
							<div className="plan-item-head">
								<span className="plan-item-name">{itemName ? `${itemName}${chineseOrdinal(i + 1)}` : `${i + 1}.`}</span>
								{!locked && (
									<span className="plan-item-actions">
									<button type="button" title="上移" disabled={i === 0} onClick={() => setItems((prev) => moveItem(prev, i, -1))}>
										↑
									</button>
									<button type="button" title="下移" disabled={i === shownItems.length - 1} onClick={() => setItems((prev) => moveItem(prev, i, 1))}>
										↓
									</button>
									<button type="button" title="删除" onClick={() => setItems((prev) => removeItem(prev, i))}>
										×
									</button>
									</span>
								)}
							</div>
							<label className="plan-item-field">
								{nameLabel && <span>{nameLabel}</span>}
								<input type="text" value={item.name} disabled={locked} onChange={(e) => editItem(i, { name: e.target.value })} />
							</label>
							<label className="plan-item-field">
								{detailLabel && <span>{detailLabel}</span>}
								<input type="text" value={item.detail} disabled={locked} onChange={(e) => editItem(i, { detail: e.target.value })} />
							</label>
						</li>
					))}
				</ol>
				{!locked && (
					<button type="button" className="plan-item-add" onClick={() => setItems((prev) => [...prev, { name: "", detail: "" }])}>
						+ 添加
					</button>
				)}
			</div>

			{gone && <p className="hint">{GONE_NOTICE.form}</p>}
			{answered?.kind === "unparseable" && <p className="error">已提交，无法读取提交内容</p>}
			{answered === undefined && submit.phase === "failed" && !gone && <p className="error">{submit.message}</p>}
			{answered === undefined && !gone && (
				<button onClick={() => void onSubmit()} disabled={locked || title.trim() === ""}>
					{submit.phase === "submitting" ? "提交中…" : submit.phase === "submitted" ? "已确认" : "确认"}
				</button>
			)}
		</div>
	);
}
